import { useContext, useCallback, useMemo } from "react";
import { PlanBuilderContext, Task, TaskGroupEdit } from "@/Components/Plan/Plan";

export interface UseTaskMenuLogicReturn {
    // State
    allowSetDependencies: boolean;
    allowRemove: boolean;
    allowCascade: boolean;
    allowSelected: boolean;
    allowDependents: boolean;
    canIndent: { canIndent: boolean; reason?: string; parentTask?: Task | undefined };
    canOutdent: { canOutdent: boolean; reason?: string };
    allowDelete: boolean;
    canComplete: { canComplete: boolean; reason?: string };

    // Handlers
    handleSetDependencies: (dependenciesType: "selected" | "dependents" | "cascade" | "remove") => void;
    handleSetTaskType: (type: "task" | "deliverable" | "milestone" | "deliverableMilestone") => void;
    handleSetFlag: (flagType: "charter" | "statusReport") => void;
    handleDelete: () => void;
    handleCompleteTask: () => void;
}

export function useTaskMenuLogic(): UseTaskMenuLogicReturn {
    const plan = useContext(PlanBuilderContext);

    const { 
    selectedTaskID, 
    selectedTaskIDs, 
    selectedWorkpackageID, 
    multiSelect,
    getTaskById,
    canCompleteTask, 
    getDependentTaskIds,
    canIndentTask, 
    canOutdentTask,
    groupEditTasks, 
    confirmDeleteWorkPackage,
    confirmDeleteTasks,
    completeMultipleTasks,
} = plan;

    // Calculate allowDelete
    const allowDelete = useMemo(() => {
        return selectedTaskID !== null || selectedWorkpackageID !== null;
    }, [selectedTaskID, selectedWorkpackageID]);

    // Calculate canComplete
    const canComplete = useMemo(() => {
        if (selectedTaskID === null) {
            return { canComplete: false, reason: "No Tasks Selected" };
        }
        
        const taskIds = selectedTaskIDs.length > 0 ? selectedTaskIDs : [selectedTaskID];
        const results = taskIds.map(taskId => canCompleteTask(taskId, false));
        const allCanComplete = results.every(result => result.canComplete);
        const firstFailureReason = results.find(result => !result.canComplete)?.reason;
        
        return { 
            canComplete: allCanComplete, 
            reason: allCanComplete ? "" : firstFailureReason 
        };
    }, [selectedTaskID, selectedTaskIDs, canCompleteTask]);

    // Calculate dependency options
    const { allowCascade, allowSelected, allowDependents, allowRemove, allowSetDependencies } = useMemo(() => {
        if (!selectedTaskID) {
            return { allowCascade: false, allowSelected: false, allowDependents: false, allowRemove: false, allowSetDependencies: false };
        }

        let removeDependencies = true;
        // these features are only available when multi-selecting
        let cascade = multiSelect && selectedTaskIDs.length > 1;
        let selected = multiSelect && selectedTaskIDs.length > 1;
        let dependents = multiSelect && selectedTaskIDs.length > 1;

        let previousTaskId = -1;
        let previousTaskDependents: number[] = [];

        selectedTaskIDs.forEach((id) => {
            const taskDependents: number[] = [];
            getDependentTaskIds(id, taskDependents);

            if (taskDependents.includes(previousTaskId) || previousTaskDependents.includes(id)) {
                cascade = false;
            }

            if (selectedTaskID && taskDependents.includes(selectedTaskID)) {
                dependents = false;
            }

            if (selectedTaskID && id === selectedTaskID
                && taskDependents.some(depId => selectedTaskIDs.includes(depId) && depId !== id)) {
                selected = false;
            }

            previousTaskId = id;
            previousTaskDependents = [...taskDependents];
        });

        const selectedTask = getTaskById(selectedTaskID);
        if (selectedTask == null || selectedTask.dependencies.length === 0) {
            removeDependencies = false;
        }
        const allowOverall = cascade || selected || dependents || removeDependencies;
        return { allowCascade: cascade, allowSelected: selected, allowDependents: dependents, allowRemove: removeDependencies, allowSetDependencies: allowOverall };
    }, [selectedTaskID, selectedTaskIDs, getDependentTaskIds, multiSelect, getTaskById]);

    // Calculate indent/outdent
    const canIndent = useMemo(() => {
        if (selectedTaskID === null) {
            return { canIndent: false, reason: "No Task Selected" };
        }
        const taskIds = selectedTaskIDs.length > 0 ? selectedTaskIDs : [selectedTaskID];
        return canIndentTask(taskIds);
    }, [selectedTaskID, selectedTaskIDs, canIndentTask]);

    const canOutdent = useMemo(() => {
        if (selectedTaskID === null) {
            return { canOutdent: false, reason: "No Task Selected" };
        }
        const taskIds = selectedTaskIDs.length > 0 ? selectedTaskIDs : [selectedTaskID];
        return canOutdentTask(taskIds);
    }, [selectedTaskID, selectedTaskIDs, canOutdentTask]);

    const handleSetDependencies = useCallback((dependenciesType: "selected" | "dependents" | "cascade" | "remove") => {
        const taskGroupEdit: TaskGroupEdit = {
            highlightedTaskId: selectedTaskID!, // can't be null, because this form is only shown when a task is selected
            taskIDs: selectedTaskIDs.length > 0 ? selectedTaskIDs : [selectedTaskID!],
            enableOwnerId: false,
            enableWorkPackage: false,
            enableIsDeliverable: false,
            enableIsMilestone: false,
            enableDisplayOnCharter: false,
            enableDisplayOnStatusReport: false,
            enableDuration: false,
            enableSetDependencies: true,
            enableSetSubtasks: false,
            ownerId: "",
            workstream: 0,
            workPackage: 0,
            isDeliverable: false,
            isMilestone: false,
            displayOnCharter: false,
            displayOnStatusReport: false,
            duration: 1,
            setDependencies: dependenciesType,
            setSubtasks: false,
        }
        groupEditTasks(taskGroupEdit);
    }, [selectedTaskID, selectedTaskIDs, groupEditTasks]);

    const handleSetTaskType = useCallback((type: "task" | "deliverable" | "milestone" | "deliverableMilestone") => {
        const taskGroupEdit: TaskGroupEdit = {
            highlightedTaskId: selectedTaskID!, // can't be null, because this form is only shown when a task is selected
            taskIDs: selectedTaskIDs.length > 0 ? selectedTaskIDs : [selectedTaskID!],
            enableOwnerId: false,
            enableWorkPackage: false,
            enableIsDeliverable: true,
            enableIsMilestone: true,
            enableDisplayOnCharter: false,
            enableDisplayOnStatusReport: false,
            enableDuration: false,
            enableSetDependencies: false,
            enableSetSubtasks: false,
            ownerId: "",
            workstream: 0,
            workPackage: 0,
            isDeliverable: type === "deliverable" || type === "deliverableMilestone",
            isMilestone: type === "milestone" || type === "deliverableMilestone",
            displayOnCharter: false,
            displayOnStatusReport: false,
            duration: 1,
            setDependencies: "remove",
            setSubtasks: false,
        }
        groupEditTasks(taskGroupEdit);
    }, [selectedTaskID, selectedTaskIDs, groupEditTasks]);

    const handleSetFlag = useCallback((flagType: "charter" | "statusReport") => {
        const taskGroupEdit: TaskGroupEdit = {
            highlightedTaskId: selectedTaskID!, // can't be null, because this form is only shown when a task is selected
            taskIDs: selectedTaskIDs.length > 0 ? selectedTaskIDs : [selectedTaskID!],
            enableOwnerId: false,
            enableWorkPackage: false,
            enableIsDeliverable: false,
            enableIsMilestone: false,
            enableDisplayOnCharter: flagType === "charter",
            enableDisplayOnStatusReport: flagType === "statusReport",
            enableDuration: false,
            enableSetDependencies: false,
            enableSetSubtasks: false,
            ownerId: "",
            workstream: 0,
            workPackage: 0,
            isDeliverable: false,
            isMilestone: false,
            displayOnCharter: flagType === "charter",
            displayOnStatusReport: flagType === "statusReport",
            duration: 1,
            setDependencies: "remove",
            setSubtasks: false,
        }
        groupEditTasks(taskGroupEdit);
    }, [selectedTaskID, selectedTaskIDs, groupEditTasks]);

    const handleDeleteWorkPackage = useCallback(() => {
        if (selectedWorkpackageID !== null) {
            // Use setTimeout to ensure the menu closes before deletion starts
            setTimeout(() => {
                confirmDeleteWorkPackage(selectedWorkpackageID!);
            }, 0);
        }
    }, [selectedWorkpackageID, confirmDeleteWorkPackage]);

    const handleDeleteTask = useCallback(() => {
        const tasks = multiSelect ? selectedTaskIDs : [selectedTaskID];
        const tasksToDelete = tasks.filter((id): id is number => id !== null);

        // Use setTimeout to ensure the menu closes before deletion starts
        setTimeout(() => {
            confirmDeleteTasks(tasksToDelete);
        }, 0);
    }, [selectedTaskID, selectedTaskIDs, multiSelect, confirmDeleteTasks]);

    const handleDelete = useCallback(() => {
        if (selectedTaskID !== null) {
            handleDeleteTask();
        } else if (selectedWorkpackageID !== null) {
            handleDeleteWorkPackage();
        }
    }, [selectedTaskID, selectedWorkpackageID, handleDeleteTask, handleDeleteWorkPackage]);

    const handleCompleteTask = useCallback(() => {
        const taskIds = selectedTaskIDs.length > 0 ? selectedTaskIDs : selectedTaskID ? [selectedTaskID] : null;
        if (!taskIds) return;
        completeMultipleTasks(taskIds);
    }, [selectedTaskID, selectedTaskIDs, completeMultipleTasks]);

    return {
        allowSetDependencies,
        allowRemove,
        allowCascade,
        allowSelected,
        allowDependents,
        canIndent,
        canOutdent,
        allowDelete,
        canComplete,
        handleSetDependencies,
        handleSetTaskType,
        handleSetFlag,
        handleDelete,
        handleCompleteTask,
    };
}
